// The "which event types does this group offer?" control, shared by the create
// dialog (views/groups.ts) and the manage form (views/groupManage.ts).
//
// It was a flat checkbox list duplicated in both files. That was fine at three
// types and would not survive a dozen: the plan is for `game` to accumulate
// poker, mahjong, dominos, D&D and so on, each a registry entry with its own
// behavior. See docs/domain.md, "Event types, families, and the picker".
//
// Three rules the shape encodes, all decided 2026-09-02:
//
//  1. A FAMILY IS A HEADING, NOT A CHOICE. It is never stored and never
//     submitted; only leaf types are. So it renders as a <details> disclosure
//     rather than a checkbox — there is no such thing as "allowing games" in
//     general, only allowing poker.
//  2. `generic` IS SHOWN BUT LOCKED. Any group can always put a plain event on
//     the calendar; it is the floor, not a feature to opt into. Hiding it
//     entirely tested badly in the obvious way — an organizer had no way to
//     learn plain events exist — so it renders as a checked, disabled row
//     labelled "always on". Disabled means it is never submitted either, so
//     the server's isEventTypeAllowed() stays the only thing that grants it.
//  3. NOTHING IS CHECKED BY DEFAULT on a new group. Checking nothing is a valid
//     answer meaning "we only do plain events", so this control has no
//     "pick at least one" validation. Do not add one back: with generic
//     excluded from the list it would make the form unsubmittable.
//
// Native <details> is deliberate over a hand-rolled expander: keyboard and
// screen-reader behavior come free, and it needs no JS to toggle.

import { el } from '../dom.js';
import {
  ALWAYS_ALLOWED_EVENT_TYPES,
  EVENT_FAMILIES,
  EVENT_FAMILY_KEYS,
  EVENT_TYPES,
  eventTypesInFamily,
  type EventTypeKey,
} from '@msc/shared';

export interface EventTypePicker {
  /** Drop this straight into the form. */
  element: HTMLElement;
  /** The leaf keys currently checked. Never includes always-allowed types. */
  value(): EventTypeKey[];
}

export interface EventTypePickerOptions {
  /**
   * The group's current `allowed_event_types`. `null` means open, so every box
   * is checked — that is what NULL has always meant and a manage form must show
   * it honestly. Pass `[]` for a new group: nothing checked.
   */
  selected: readonly EventTypeKey[] | null;
  /** Prefix for input ids, so two pickers on one page cannot collide. */
  idPrefix: string;
}

export function eventTypePicker(opts: EventTypePickerOptions): EventTypePicker {
  const { selected, idPrefix } = opts;
  const isChecked = (key: EventTypeKey): boolean =>
    selected === null || selected.includes(key);

  const boxes: { key: EventTypeKey; box: HTMLInputElement }[] = [];

  // The always-on rows sit above the families, unfamilied and unselectable.
  // `disabled` is doing real work: it keeps them out of the submitted set, so
  // value() cannot accidentally start writing them into allowed_event_types.
  const sections: HTMLElement[] = ALWAYS_ALLOWED_EVENT_TYPES.map((key) => {
    const locked = el('input', { type: 'checkbox', disabled: true }) as HTMLInputElement;
    locked.checked = true;
    return el('label', { class: 'check event-type-locked' }, [
      locked,
      ` ${EVENT_TYPES[key].icon} ${EVENT_TYPES[key].label} `,
      el('span', { class: 'muted small' }, '(always on)'),
    ]);
  });

  for (const familyKey of EVENT_FAMILY_KEYS) {
    const family = EVENT_FAMILIES[familyKey];
    const keys = eventTypesInFamily(familyKey);
    // A family with nothing under it is a registry mistake, not a UI state.
    // Skip it rather than rendering an expander that opens onto nothing.
    if (keys.length === 0) continue;

    const rows = keys.map((key) => {
      const id = `${idPrefix}-et-${key}`;
      const box = el('input', {
        type: 'checkbox',
        id,
        value: key,
      }) as HTMLInputElement;
      box.checked = isChecked(key);
      boxes.push({ key, box });
      return el('label', { class: 'check', for: id }, [
        box,
        ` ${EVENT_TYPES[key].icon} ${EVENT_TYPES[key].label}`,
      ]);
    });

    const details = el('details', { class: 'event-family' }, [
      el('summary', {}, `${family.icon} ${family.label}`),
      el('p', { class: 'muted small event-family-hint' }, family.hint),
      el('div', { class: 'event-family-types' }, rows),
    ]) as HTMLDetailsElement;

    // Open a section that already has something checked, so an existing group's
    // settings are visible without hunting. A new group opens nothing.
    details.open = keys.some((k) => isChecked(k));
    sections.push(details);
  }

  const element = el('div', { class: 'event-type-picker' }, sections);

  return {
    element,
    value: () => boxes.filter((b) => b.box.checked).map((b) => b.key),
  };
}
